import { QuillBinding } from "y-quill"
import { SSEProvider } from "./yjs-utils/sse-provider"
import Quill, { QuillOptions } from "quill"
import { Accessor, createEffect, createMemo, createSignal, on, Setter } from "solid-js"
import { collaborativeEdit as api, CursorInfo } from 'st-app/api'
import * as Y from 'yjs'
import QuillCursors from "quill-cursors"
import { uuidv4 } from "common"
import { createStore, SetStoreFunction } from "solid-js/store"

export type EditSource = 'user' | 'collaborator'

export interface Collaborator {
    id: number 
    name: string
    color: string
    initials: string
    online: boolean
    active: boolean
}

// We don't need to re-load the cursor info for every new textbox, so we can just keep it in memory
let cachedCursorInfo: CursorInfo | null = null

export type ValueSetHandler = (value: string, source: EditSource) => void
export type PropertySavedHandler = (value: string) => void
export type FocusChangeHandler = (focused: boolean) => void
export type AutosaveHandler = (value: string) => void
export type PresenceChangeHandler = (cursors: Collaborator[]) => void

export class CollaborativeEditContext {
    collaborators: Accessor<Collaborator[]>; setCollaborators: Setter<Collaborator[]>;
    activeCollaborators: Accessor<Collaborator[]>;
    onPropertySaved: PropertySavedHandler | undefined;
    onValueSet: ValueSetHandler | undefined;
    isRichText: boolean = false;
    isFocused: Accessor<boolean>; setIsFocused: Setter<boolean>;
    isSaving: Accessor<boolean>; setIsSaving: Setter<boolean>;
    isFullscreen: Accessor<boolean>; setIsFullscreen: Setter<boolean>;
    value: Accessor<string>; setValue: Setter<string>;
    srcName: Accessor<string>;
    identity: Accessor<string>;
    readonly: Accessor<boolean>;
    key: Accessor<string>;
    container: Accessor<HTMLDivElement | null>; setContainer: Setter<HTMLDivElement | null>;

    session: CollaborativeEditSession | null = null;

    constructor(srcName: Accessor<string>, identity: Accessor<string>, readonly: Accessor<boolean>, isRichText: boolean, onValueSet?: ValueSetHandler, onPropertySaved?: PropertySavedHandler) {
        [this.container, this.setContainer] = createSignal<HTMLDivElement | null>(null);
        [this.collaborators, this.setCollaborators] = createSignal<Collaborator[]>([]);
        [this.isFocused, this.setIsFocused] = createSignal<boolean>(false);
        [this.isFullscreen, this.setIsFullscreen] = createSignal<boolean>(false);
        [this.value, this.setValue] = createSignal<string>('');
        [this.isSaving, this.setIsSaving] = createSignal<boolean>(false);
        this.srcName = srcName;
        this.identity = identity;
        this.readonly = readonly;
        this.onPropertySaved = onPropertySaved;
        this.onValueSet = onValueSet;
        this.activeCollaborators = createMemo(() => this.collaborators().filter(c => c.active && c.online && c.id !== this.session?.provider?.awareness.clientID));
        this.key = createMemo(() => `${this.srcName()}:${this.identity()}`)
        this.isRichText = isRichText;

        createEffect(on(() => [this.srcName(), this.identity(), this.container()], () => {
            if (!this.container())
                return;

            if (this.session) {
                this.session.destroy();
                this.session = null;
            }

            if (!this.srcName() || !this.identity())
                return;

            this.session = new CollaborativeEditSession({
                srcName: this.srcName(),
                identity: this.identity(),
                readonly: this.readonly(),
                richtext: this.isRichText,
                onAutosave: (v) => {
                    this.setValue(v);
                    this.save();
                },
                onFocusChange: (focused) => this.setIsFocused(focused),
                onValueSet: (v, source) => {
                    this.setValue(v);
                    this.onValueSet?.(v, source);
                },
                onPropertySaved: (v) => this.onPropertySaved?.(v),
                onPresenceChange: (c) => {
                    this.setCollaborators(c)
                },
            });

            this.session.bootstrap(this.container()!);
        }))
    }

    async save() {
        this.setIsSaving(true)
        try {
            const minDelay = new Promise((resolve) => setTimeout(resolve, 1000))
            const savePromise = api.SaveValue(this.srcName(), this.identity(), this.value(), this.session?.snapshot ?? '')
            await Promise.all([savePromise, minDelay])
        } catch (e) {
            console.error('Error saving value', e)
        }
        this.setIsSaving(false)
    }
}

export interface CollaborativeEditSessionOptions {
    srcName: string
    identity: string
    readonly?: boolean
    richtext?: boolean
    onValueSet?: ValueSetHandler
    onPropertySaved?: PropertySavedHandler
    onFocusChange?: FocusChangeHandler
    onAutosave?: AutosaveHandler
    onPresenceChange?: PresenceChangeHandler
}

export class CollaborativeEditSession {
    id: number;
    srcName: string;
    identity: string;
    key: string
    ydoc?: Y.Doc
    provider?: SSEProvider
    editor?: Quill
    binding?: QuillBinding
    onValueSet?: ValueSetHandler
    onPropertySaved?: PropertySavedHandler
    onFocusChange?: FocusChangeHandler
    onAutosave?: AutosaveHandler
    onPresenceChange?: PresenceChangeHandler

    richtext: boolean = false
    readonly: boolean = false
    focused: boolean = false

    private cloudSyncTimer: number | undefined = undefined;
    private heartbeatTimer: number | undefined = undefined;

    constructor(options: CollaborativeEditSessionOptions) {
        this.id = Math.floor(Math.random() * 1000000);
        this.srcName = options.srcName;
        this.identity = options.identity;
        this.key = `${this.srcName}:${this.identity}`;
        this.onValueSet = options.onValueSet;
        this.onFocusChange = options.onFocusChange;
        this.onPropertySaved = options.onPropertySaved;
        this.onAutosave = options.onAutosave;
        this.onPresenceChange = options.onPresenceChange;
        this.richtext = options.richtext || false;
        this.readonly = options.readonly || false;
    }

    get snapshot() {
        if (!this.ydoc) return null;
        const state = Y.encodeStateAsUpdateV2(this.ydoc);
        return btoa(String.fromCharCode(...state));
    }

    async bootstrap(container: HTMLDivElement) {
        const value = await api.GetCurrentValue(this.srcName, this.identity) || '';
        const temporaryDoc = new Y.Doc();
        const ytext = temporaryDoc.getText('quill');
        ytext.insert(0, value);
        const state = Y.encodeStateAsUpdateV2(temporaryDoc);
        const stateBase64 = btoa(String.fromCharCode(...state));

        const checkpoint = await api.Bootstrap({
            key: this.key,
            snapshotBase64: stateBase64,
            value: value,
        })

        await api.JoinCollaboration(this.key, Date.now());

        this.ydoc = new Y.Doc();
        if (checkpoint?.snapshotBase64) {
            Y.applyUpdateV2(this.ydoc, Uint8Array.from(atob(checkpoint.snapshotBase64), c => c.charCodeAt(0)));
        }

        this.editor = this.initQuill(container)
        this.provider = new SSEProvider(this.key, this.ydoc, { id: this.id })
        this.binding = new QuillBinding(this.ydoc.getText('quill'), this.editor, this.provider.awareness);

        this.getCursorInfo().then((cursor) => this.provider?.updatePresence(cursor))

        this.provider.awareness.on('change', () => {
            const states = this.provider?.awareness.getStates()
            if (!states) return

            const collaborators = [...states.entries()]
                .map(([id, state]) => this.buildCollaborator(id, state));
    
            this.onPresenceChange?.(collaborators);
        })

        this.heartbeatTimer = window.setInterval(() => {
            queueHeartbeat(this.key);
        }, 10000);
    }
    
    initQuill(container: HTMLDivElement): Quill {
        const el = document.createElement('div')
        container.replaceChildren(el)
        if (this.cloudSyncTimer) {
            clearTimeout(this.cloudSyncTimer)
            this.cloudSyncTimer = undefined;
        }

        const quillOptions = this.buildQuillOptions();
        const editor = new Quill(el, quillOptions)

        editor.on('text-change', (delta, oldContents, source) => {
            const value = this.richtext ? editor.getSemanticHTML() : editor.getText() ?? ''
            
            const trimmedValue = value.endsWith('\n') ? value.slice(0, -1) : value
            this.onValueSet?.(trimmedValue, source === 'user' ? 'user' : 'collaborator')

            if (source === 'user') {
                if (this.cloudSyncTimer) {
                    clearTimeout(this.cloudSyncTimer)
                }

                this.cloudSyncTimer = window.setTimeout(() => {
                    if (this.focused) {
                        this.onAutosave?.(trimmedValue);
                    }
                }, 2000)
            }
        })

        editor.on('selection-change', (range, oldRange, source) => {
            if (!this.readonly) {
                this.focused = !!range
                this.onFocusChange?.(this.focused);
            }
        })

        return editor
    }

    private buildQuillOptions(): QuillOptions {
        const quillOptions: QuillOptions = {
            modules: {
                cursors: true,
                toolbar: false,
                history: {
                    userOnly: true
                }
            },
            formats: [],
            theme: 'snow',
        }

        if (this.richtext) {
            quillOptions.modules!['toolbar'] = [
                [{ header: [1, 2, false] }],
                ['bold', 'italic', 'underline'],
                ['image', 'code-block'],
            ]
            quillOptions.formats = ['bold', 'italic', 'underline', 'header', 'image', 'code-block']
        }
        return quillOptions;
    }

    async getCursorInfo() {
        if (!cachedCursorInfo) {
            cachedCursorInfo = await api.GetCursorInfo()
        }
        return cachedCursorInfo;
    }

    buildCollaborator(id: number, state: { [x: string]: any }): Collaborator {
        const online = !!(state?.user)
        const name = state?.user?.name ?? 'Unknown'
        const color = state?.user?.color ?? '#000000'
        const initials = name.split(' ').map((n: string) => n[0]).join('').toUpperCase()
        const active = !!state?.cursor;
        return { id, name, color, initials, online, active }
    }

    async destroy() {
        this.provider?.destroy();
        this.ydoc?.destroy();
        this.binding?.destroy();
        this.provider = undefined;
        this.ydoc = undefined;
        this.binding = undefined;
        this.editor = undefined;

        if (this.cloudSyncTimer) {
            clearTimeout(this.cloudSyncTimer)
            this.cloudSyncTimer = undefined;
        }

        if (this.heartbeatTimer) {
            clearInterval(this.heartbeatTimer)
            this.heartbeatTimer = undefined;
        }

        try {
            await api.LeaveCollaboration(this.key, Date.now())
        } catch (e) { console.error('Error leaving collaboration', e) }

    }
}

const heartbeatQueue: Set<string> = new Set();

function queueHeartbeat(key: string) {
    heartbeatQueue.add(key);
}

setInterval(() => {
    try {
        if (heartbeatQueue.size === 0) return;
        const keys = Array.from(heartbeatQueue);
        heartbeatQueue.clear();
        api.Heartbeat(keys);
    } catch (e) {
        console.error('Error sending heartbeat', e);
    }
}, 10000);